import { z } from 'zod/v4';

import { escapeRegExp, regEx } from '../../../util/regex.ts';
import { DockerDatasource } from '../../datasource/docker/index.ts';
import { GithubReleaseAttachmentsDatasource } from '../../datasource/github-release-attachments/index.ts';
import { GithubReleasesDatasource } from '../../datasource/github-releases/index.ts';
import { NpmDatasource } from '../../datasource/npm/index.ts';
import { PypiDatasource } from '../../datasource/pypi/index.ts';
import { RubyVersionDatasource } from '../../datasource/ruby-version/index.ts';
import { RustVersionDatasource } from '../../datasource/rust-version/index.ts';
import * as condaVersioning from '../../versioning/conda/index.ts';
import * as npmVersioning from '../../versioning/npm/index.ts';
import type { PackageDependency } from '../types.ts';

export interface CommunityActionConfig {
  datasource: string;
  depName?: string;
  packageName: string;
  versioning?: string;
  extractVersion?: string;

  /**
   * should return `true` if the version is invalid and should be skipped
   */
  isInvalid?: (value: string) => boolean;

  withSchema?: z.ZodType<{ val: string | undefined } & Record<string, unknown>>;
}

export type ActionSchema = z.ZodType<PackageDependency>;

export function actionSchema(
  name: string,
  { isInvalid, withSchema, ...cfg }: CommunityActionConfig,
): ActionSchema {
  return z
    .object({
      uses: matchAction(name),
      with: withSchema ?? VersionVal,
    })
    .transform(
      ({ with: { val, ...meta } }): PackageDependency => ({
        ...cfg,
        ...meta,
        ...parseValue(val, isInvalid),
      }),
    )
    .transform((dep) => {
      dep.depName ??= dep.packageName;
      return dep;
    });
}

function matchAction(action: string): z.ZodString {
  return z
    .string()
    .regex(regEx(`(?:https?://[^/]+/)?${escapeRegExp(action)}(?:@.+)?$`));
}

function parseValue(
  currentValue: string | undefined,
  isInvalid?: (val: string) => boolean,
): PackageDependency {
  if (!currentValue) {
    return {
      skipStage: 'extract',
      skipReason: 'unspecified-version',
      depType: 'uses-with',
    };
  }
  if (isInvalid?.(currentValue) === true) {
    return {
      skipStage: 'extract',
      skipReason: 'invalid-version',
      depType: 'uses-with',
      currentValue,
    };
  }
  return { currentValue, depType: 'uses-with' };
}

function valSchema(key: string): z.ZodType<{ val: string | undefined }> {
  return z
    .object({ [key]: z.string().optional() })
    .transform((val) => ({ val: val[key] }));
}

const VersionVal = z
  .object({ version: z.string().optional() })
  .transform((val) => ({ val: val.version }));

const InstallBinaryWith = z
  .object({ repo: z.string(), tag: z.string() })
  .transform(({ repo, tag }) => ({ packageName: repo, val: tag }));

const sha256Regex = regEx(/^[a-f0-9]{64}$/);
const MiseWith = z
  .object({
    version: z.string().optional(),
    sha256: z.string().optional(),
  })
  .transform(({ version, sha256 }) => ({
    val: version,
    ...(sha256 && sha256Regex.test(sha256) ? { currentDigest: sha256 } : {}),
  }));

/**
 * Community contributed actions with known version input schemas.
 */
export const communityActions: Record<string, CommunityActionConfig> = {
  // https://github.com/aquasecurity/setup-trivy
  'aquasecurity/setup-trivy': {
    datasource: GithubReleasesDatasource.id,
    packageName: 'aquasecurity/trivy',
  },
  // https://github.com/aquasecurity/trivy-action
  'aquasecurity/trivy-action': {
    datasource: GithubReleasesDatasource.id,
    packageName: 'aquasecurity/trivy',
  },
  // https://github.com/astral-sh/setup-uv
  'astral-sh/setup-uv': {
    datasource: GithubReleasesDatasource.id,
    versioning: npmVersioning.id,
    packageName: 'astral-sh/uv',
  },
  'azure/setup-helm': {
    datasource: GithubReleasesDatasource.id,
    depName: 'helm',
    packageName: 'helm/helm',
  },
  // https://github.com/azure/setup-helm
  'denoland/setup-deno': {
    datasource: NpmDatasource.id,
    packageName: 'deno',
    withSchema: valSchema('deno-version'),
  },
  // https://github.com/docker/setup-buildx-action
  'docker/setup-buildx-action': {
    datasource: GithubReleasesDatasource.id,
    depName: 'buildx',
    packageName: 'docker/buildx',
  },
  // https://github.com/docker/setup-compose-action
  'docker/setup-compose-action': {
    datasource: GithubReleasesDatasource.id,
    packageName: 'docker/compose',
  },
  // https://github.com/docker/setup-docker-action
  'docker/setup-docker-action': {
    datasource: GithubReleasesDatasource.id,
    depName: 'docker',
    packageName: 'moby/moby',
    extractVersion: '^docker-(?<version>.+)$',
  },
  // https://github.com/dtolnay/rust-toolchain
  'dtolnay/rust-toolchain': {
    datasource: RustVersionDatasource.id,
    packageName: 'rust',
    withSchema: valSchema('toolchain'),
  },
  'golangci/golangci-lint-action': {
    datasource: GithubReleasesDatasource.id,
    packageName: 'golangci/golangci-lint',
  },
  // https://github.com/helm/chart-testing-action
  'helm/chart-testing-action': {
    datasource: GithubReleasesDatasource.id,
    depName: 'chart-testing',
    packageName: 'helm/chart-testing',
  },
  'jakebailey/pyright-action': {
    datasource: NpmDatasource.id,
    packageName: 'pyright',
    isInvalid: (val) => val === 'PATH',
  },
  'jaxxstorm/action-install-gh-release': {
    datasource: GithubReleasesDatasource.id,
    packageName: '', // determined from `repo` input
    withSchema: InstallBinaryWith,
  },
  'jdx/mise-action': {
    datasource: GithubReleaseAttachmentsDatasource.id,
    packageName: 'jdx/mise',
    withSchema: MiseWith,
  },
  'oven-sh/setup-bun': {
    datasource: NpmDatasource.id,
    packageName: 'bun',
    withSchema: valSchema('bun-version'),
  },
  'pdm-project/setup-pdm': {
    datasource: PypiDatasource.id,
    packageName: 'pdm',
  },
  'pnpm/action-setup': {
    datasource: NpmDatasource.id,
    packageName: 'pnpm',
  },
  'prefix-dev/setup-pixi': {
    datasource: GithubReleasesDatasource.id,
    versioning: condaVersioning.id,
    packageName: 'prefix-dev/pixi',
    withSchema: valSchema('pixi-version'),
  },
  // https://github.com/pypa/hatch/tree/install
  'pypa/hatch': {
    datasource: GithubReleasesDatasource.id,
    packageName: 'pypa/hatch',
    // Strip hatch- prefix from release tags
    extractVersion: '^hatch-(?<version>.+)$',
  },
  'ruby/setup-ruby': {
    datasource: RubyVersionDatasource.id,
    packageName: 'ruby',
    withSchema: valSchema('ruby-version'),
  },
  'sigoden/install-binary': {
    datasource: GithubReleasesDatasource.id,
    packageName: '', // determined from `repo` input
    withSchema: InstallBinaryWith,
  },
  'sigstore/cosign-installer': {
    datasource: GithubReleasesDatasource.id,
    packageName: 'sigstore/cosign',
    withSchema: valSchema('cosign-release'),
  },
  'zizmorcore/zizmor-action': {
    datasource: DockerDatasource.id,
    packageName: 'ghcr.io/zizmorcore/zizmor',
  },
};
